import Dexie from 'dexie';

/**
 * Schéma de la base de données locale (osc_datadb)
 * 
 * Ce schéma est conçu pour gérer différents types d'activités commerciales :
 * - Restaurant
 * - Boulangerie
 * - Coiffure
 * - Auto-école
 * - Magasin de vêtements
 * - Service de réparation
 * - Accessoires électroniques
 * - Épicerie
 * - Atelier (menuisier, aluminium, garagiste)
 * 
 * Structure générale :
 * 1. Tables de base communes à toutes les activités :
 *    - users : Gestion des utilisateurs
 *    - products : Produits et services
 *    - categories : Catégories de produits
 *    - customers : Clients
 *    - sales : Ventes
 *    - saleItems : Items de vente
 *    - payments : Paiements
 *    - approvisionnements : Gestion des stocks
 *    - settings : Configuration système
 *    - activity_settings : Configuration spécifique à l'activité
 *    - modelesVente : Modèles de vente personnalisés
 *    - champsCustomValues : Champs personnalisés
 * 
 * 2. Tables spécifiques à certaines activités :
 *    - tables : Gestion des tables (restaurant)
 *    - appointments : Gestion des rendez-vous (coiffure, auto-école, réparation)
 *    - commandes : Gestion des commandes (atelier)
 * 
 * 3. Gestion des produits et services :
 *    - Un produit peut être un service (isService = true)
 *    - Un produit peut être un pack (packItems contient la composition)
 *    - Les catégories ont un type (matiere_premiere, piece_detachee, service, produit_fini)
 *    - Chaque produit a un taux de TVA
 * 
 * 4. Gestion des ventes :
 *    - Une vente peut être liée à un utilisateur (vendeur)
 *    - Une vente peut être liée à une table (restaurant) ou un rendez-vous (coiffure, auto-école)
 *    - Les items de vente gardent une trace du nom et du taux de TVA
 *    - Les paiements peuvent être de différents types (acompte, solde, etc.)
 * 
 * 5. Gestion des stocks :
 *    - Les approvisionnements sont liés à un utilisateur
 *    - Chaque mouvement a un coût unitaire et total
 *    - Les mouvements peuvent être de différents types
 * 
 * 6. Personnalisation par activité :
 *    - champsCustomValues permet d'ajouter des champs spécifiques
 *    - modelesVente permet de définir des modèles de vente personnalisés
 *    - settings permet de configurer le système
 *    - activity_settings permet de configurer l'activité spécifique
 * 
 * Exemples d'utilisation :
 * 
 * 1. Pour un restaurant :
 *    - Products : Plats, boissons
 *    - Categories : Entrées, plats, desserts
 *    - Tables : Gestion des tables et des commandes
 *    - Sales : Ventes avec tableId pour lier à une table
 * 
 * 2. Pour un atelier :
 *    - Products : Services avec packItems (matériaux)
 *    - Categories : Matières premières, pièces détachées, services
 *    - Commandes : Gestion des commandes et devis
 *    - Sales : Ventes avec commandeId en champsCustomValues
 * 
 * 3. Pour une coiffure :
 *    - Products : Services et produits
 *    - Appointments : Gestion des rendez-vous
 *    - Sales : Ventes avec appointmentId pour lier à un rendez-vous
 */

export const localSchema = {
  users: '++id, username, password, role, isActive, createdAt, updatedAt',
  products: '++id, name, description, price, cost, categoryId, categoryName, barcode, image, isActive, Type, packItems, tauxTva, stock, unite, createdAt, updatedAt, lastSync',
  categories: '++id, name, description, image, createdAt, updatedAt, lastSync',
  customers: '++id, name, phone, address, notes, isActive,solde, createdAt, updatedAt, lastSync',
  /**
   * Schéma de la table sales
   * 
   * Structure d'une vente :
   * {
   *   id: number,                    // Clé primaire auto-incrémentée
   *   num: string,                   // Numéro de vente (ex: "V20240320-001")
   *   date: Date,                    // Date de la vente
   *   sens: number,                  // Sens: 1 (vente) ou -1 (retour)
   *   
   *   customerId: number,            // ID du client
   *   customer: {                    // Snapshot des informations client
   *     name: string,
   *     phone: string,
   *     address: string
   *   },
   *   
   *   items: [{                      // Tableau des articles vendus
   *     productId: number,           // ID du produit
   *     name: string,                // Nom du produit
   *     typeProduit: string,         // Type: 'produit', 'service', 'pack', 'matiere_premiere', 'piece_detachee'
   *     unite: string,               // Unité: 'piece', 'kg', 'm', 'm2', 'm3', 'l', 'h', 'forfait'
   *     dimensions: {                // Dimensions pour le calcul de la quantité
   *       type: string,              // Type: 'volume', 'surface', 'lineaire', 'poids'
   *       length: number,            // Longueur (m)
   *       width: number,             // Largeur (m)
   *       height: number,            // Hauteur (m)
   *       weight: number,            // Poids (kg)
   *       formula: string,           // Formule de calcul (ex: "length * width" pour surface)
   *       result: number             // Résultat du calcul
   *     },
   *     quantity: number,            // Quantité (peut être calculée à partir des dimensions)
   *     costPrice: number,           // Prix de revient
   *     unitPrice: number,           // Prix unitaire
   *     remisePercent: number,       // Pourcentage de remise
   *     discount: number,            // Montant de la remise
   *     tauxTva: number,             // Taux de TVA
   *     tva: number,                 // Montant TVA
   *     total: number,               // Total TTC
   *     marge: number,               // Marge
   *     details: {                   // Détails supplémentaires de l'item
   *       type: string,              // Type: 'description' ou 'composition'
   *       content: string|array,     // Si type='description': texte (ex: "1mx0.25")
   *                                  // Si type='composition': [{productId, name, quantity}]
   *       notes: string              // Notes additionnelles
   *     }
   *   }],
   *   
   *   montant: {                     // Regroupement des montants
   *     total: number,               // Total de la vente
   *     discount: number,            // Remise globale
   *     marge: number,               // Marge totale
   *     tva: number,                 // Total TVA
   *     ht: number                   // Total HT
   *   },
   *   
   *   paiement: {                    // Regroupement des informations de paiement
   *     paymentId: number,           // ID du paiement associé
   *     datePaiement: Date,          // Date du paiement
   *     method: string,              // Mode de paiement: 'cash', 'card', 'transfer', 'check', 'mobile'
   *     status: string,              // Statut: 'pending', 'partial', 'paid', 'cancelled', 'refunded'
   *     amount: number,              // Montant payé
   *     rest: number,                // Reste à payer
   *     note: string                 // Note sur le paiement
   *   },
   *   
   *   status: string,                // Statut: 'draft', 'pending', 'completed', 'cancelled', 'refunded'
   *   notes: string,                 // Notes
   *   
   *   userId: number,                // ID de l'utilisateur
   *   tableId: number,               // ID de la table (restaurant)
   *   appointmentId: number,         // ID du rendez-vous
   *   
   *   createdAt: Date,               // Date de création
   *   updatedAt: Date,               // Date de modification
   *   lastSync: Date                 // Dernière synchronisation
   * }
   */
  sales: '++id, num, date, sens, customerId, customer, items, montant, paiement, status, notes, userId, tableId, appointmentId, createdAt, updatedAt, lastSync',
  payments: '++id, saleId, userId, amount, method, type, status, reference, notes, createdAt, updatedAt, lastSync',
  approvisionnements: '++id, productId, userId, type, quantity, cost, total, reference, notes, createdAt, updatedAt, lastSync',
  settings: '++id, key, value, type, description, isSystem, createdAt, updatedAt, lastSync',
  /**
   * Schéma de la table activity_settings
   * 
   * Structure des paramètres d'activité :
   * {
   *   id: number,                    // Clé primaire auto-incrémentée
   *   activityId: string,            // ID de l'activité
   *   
   *   businessInfo: {                // Informations du commerce
   *     businessId: string,          // Identifiant du commerce
   *     businessName: string,        // Nom du commerce
   *     logo: string                 // URL ou chemin du logo
   *   },
   *   
   *   generalSettings: {             // Paramètres généraux
   *     currency: string,            // Devise (EUR, USD, GBP, MAD)
   *     defaultLanguage: string      // Langue par défaut (fr, ar, es)
   *   },
   *   
   *   ticketNumbering: {             // Configuration de la numérotation
   *     type: string,                // Type: 'monthly', 'yearly', 'continuous'
   *     prefix: string,              // Préfixe du numéro
   *     suffix: string,              // Suffixe du numéro
   *     startNumber: number,         // Numéro de départ
   *     currentNumber: number        // Dernier numéro utilisé
   *   },
   *   
   *   createdAt: Date,               // Date de création
   *   updatedAt: Date,               // Date de modification
   *   lastSync: Date                 // Dernière synchronisation
   * }
   */
  activitySettings: '++id, businessInfo, generalSettings, ticketNumbering, createdAt, updatedAt, lastSync',
  syncQueue: '++id, table, recordId, action, data, status, error, attempts, createdAt, updatedAt',
  modelesVente: '++id, name, applicableTo, calculateur, unite, champsCustom, createdAt, updatedAt',
  champsCustomValues: '++id, table, recordId, cle, valeur, createdAt, updatedAt',
  tables: '++id, number, capacity, status, location, notes, isActive, createdAt, updatedAt, lastSync',
  appointments: '++id, customerId, userId, startTime, endTime, status, type, notes, createdAt, updatedAt, lastSync',
  commandes: '++id, customerId, userId, reference, status, total, deliveryDate, notes, createdAt, updatedAt, lastSync'
};